import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InventoryIcon from "@mui/icons-material/Inventory2";
import CalculateIcon from "@mui/icons-material/Calculate";
import SettingsIcon from "@mui/icons-material/Settings";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAuth } from "./AuthProvider";

const DRAWER_WIDTH = 240;
const BOTTOM_NAV_HEIGHT = 56;

const navItems = [
  { label: "Dashboard", path: "/dashboard", icon: <DashboardIcon /> },
  { label: "Items", path: "/items", icon: <InventoryIcon /> },
  { label: "Calculator", path: "/calculator", icon: <CalculateIcon /> },
  { label: "Settings", path: "/settings", icon: <SettingsIcon /> },
];

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { logout } = useAuth();

  const currentNavIndex = navItems.findIndex((item) =>
    location.pathname.startsWith(item.path)
  );

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 2.5,
        }}
      >
        <StorefrontIcon sx={{ color: "primary.main", fontSize: 28 }} />
        <Typography variant="h6" sx={{ color: "primary.main", fontSize: 18 }}>
          Antique Tracker
        </Typography>
      </Box>
      <Divider />
      <List sx={{ px: 1, pt: 1, flex: 1 }}>
        {navItems.map((item) => (
          <ListItemButton
            key={item.path}
            onClick={() => navigate(item.path)}
            selected={location.pathname.startsWith(item.path)}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              "&.Mui-selected": {
                backgroundColor: "primary.main",
                color: "white",
                "& .MuiListItemIcon-root": { color: "white" },
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
      <Divider />
      <List sx={{ px: 1, py: 1 }}>
        <ListItemButton
          onClick={() => { logout(); navigate("/login"); }}
          sx={{ borderRadius: 2, color: "text.secondary" }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}><LogoutIcon /></ListItemIcon>
          <ListItemText primary="Sign Out" />
        </ListItemButton>
      </List>
    </Box>
  );

  if (isMobile) {
    // Mobile: top app bar + bottom navigation
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", width: "100%" }}>
        {/* Top App Bar */}
        <AppBar position="fixed" elevation={1}>
          <Toolbar variant="dense" sx={{ minHeight: 48 }}>
            <StorefrontIcon sx={{ mr: 1, fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1 }}>
              Antique Tracker
            </Typography>
            <IconButton
              color="inherit"
              size="small"
              onClick={() => { logout(); navigate("/login"); }}
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            pt: "64px", // top bar height + breathing room
            pb: `${BOTTOM_NAV_HEIGHT + 16}px`, // bottom nav + breathing room
            px: 2,
            backgroundColor: "background.default",
            width: "100%",
            maxWidth: "100vw",
            overflowX: "hidden",
          }}
        >
          {children}
        </Box>

        {/* Bottom Navigation */}
        <Paper
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (t) => t.zIndex.appBar,
            borderTop: "1px solid",
            borderColor: "divider",
            pb: "env(safe-area-inset-bottom)",
          }}
          elevation={3}
        >
          <BottomNavigation
            value={currentNavIndex >= 0 ? currentNavIndex : 0}
            onChange={(_, newValue) => navigate(navItems[newValue].path)}
            showLabels
            sx={{ height: BOTTOM_NAV_HEIGHT }}
          >
            {navItems.map((item) => (
              <BottomNavigationAction
                key={item.path}
                label={item.label}
                icon={item.icon}
              />
            ))}
          </BottomNavigation>
        </Paper>
      </Box>
    );
  }

  // Desktop: permanent sidebar
  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          backgroundColor: "background.default",
          minHeight: "100vh",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
